/** Monta as mensagens prontas a partir dos dados do atendimento. */
import { formatDayShort, formatHour, messageDayLabel } from '../domain/format';
import { whatsappUrl } from '../domain/phone';
import { type LocalDate, DAY, addDays, todayIn, toMs, utcToZoned } from '../domain/time';
import type { Appointment, Client, MessageKind, MessageTemplate, Professional, Service } from '../schemas/entities';
import { type TemplateVars, buildMessage } from './templates';

export function bookingLink(baseUrl: string, slug: string | null | undefined): string {
  if (!slug) return '';
  return `${baseUrl.replace(/\/$/, '')}/p/${slug}`;
}

/** Link pessoal do cliente: abre direto em "Meus horários". */
export function clientLink(baseUrl: string, slug: string | null | undefined, code: string | null | undefined): string {
  if (!slug) return '';
  if (!code) return bookingLink(baseUrl, slug);
  return `${bookingLink(baseUrl, slug)}/me?code=${encodeURIComponent(code)}`;
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

export interface MessageContext {
  professional: Pick<Professional, 'timezone' | 'address' | 'slug'>;
  baseUrl: string;
  templates?: MessageTemplate[];
  now?: number;
}

export function appointmentVars(
  ctx: MessageContext,
  appointment: Pick<Appointment, 'start_utc' | 'location' | 'address'>,
  client: Pick<Client, 'name' | 'access_code'>,
  service: Pick<Service, 'name' | 'return_days'> | null,
): TemplateVars {
  const tz = ctx.professional.timezone;
  const local = utcToZoned(appointment.start_utc, tz);
  const today = todayIn(tz, ctx.now ?? Date.now());
  const address = appointment.location === 'home' ? (appointment.address ?? '') : (ctx.professional.address ?? '');
  return {
    nome: firstName(client.name),
    serviço: service?.name.toLowerCase() ?? '',
    dia: messageDayLabel(local.date, today),
    hora: formatHour(local.time),
    endereço: address,
    link: clientLink(ctx.baseUrl, ctx.professional.slug, client.access_code),
  };
}

export function appointmentMessage(
  kind: Extract<MessageKind, 'confirmation' | 'reminder' | 'reschedule' | 'delay' | 'post_service'>,
  ctx: MessageContext,
  appointment: Pick<Appointment, 'start_utc' | 'location' | 'address'>,
  client: Pick<Client, 'name' | 'access_code'>,
  service: Pick<Service, 'name' | 'return_days'> | null,
  extra: TemplateVars = {},
): string {
  const vars = appointmentVars(ctx, appointment, client, service);
  if (kind === 'post_service') {
    const days = service?.return_days ?? 21;
    const suggested = utcToZoned(toMs(appointment.start_utc) + days * DAY, ctx.professional.timezone).date;
    vars.data_sugerida = formatDayShort(suggested);
  }
  return buildMessage(kind, { ...vars, ...extra }, ctx.templates);
}

export function missingMessage(
  ctx: MessageContext,
  client: Pick<Client, 'name' | 'access_code'>,
  service: Pick<Service, 'name'> | null,
  weeks: number,
): string {
  return buildMessage(
    'missing',
    {
      nome: firstName(client.name),
      semanas: String(Math.max(1, weeks)),
      serviço: service?.name.toLowerCase() ?? 'visita',
      link: clientLink(ctx.baseUrl, ctx.professional.slug, client.access_code),
    },
    ctx.templates,
  );
}

/**
 * Dados de exemplo para a prévia dos modelos ("Como a Maria vai receber"): sempre a mesma cliente,
 * com o endereço e o link da profissional.
 */
export function exampleMessageVars(ctx: MessageContext): TemplateVars {
  return {
    nome: 'Maria',
    serviço: 'pé e mão',
    dia: 'amanhã',
    hora: '10h30',
    endereço: ctx.professional.address ?? '',
    link: clientLink(ctx.baseUrl, ctx.professional.slug, 'MARIA1') || bookingLink(ctx.baseUrl, 'seu-link'),
    minutos: '15',
    semanas: '5',
    data_sugerida: 'sexta, 06/11',
  };
}

export function openAgendaMessage(ctx: MessageContext): string {
  return buildMessage('open_agenda', { link: bookingLink(ctx.baseUrl, ctx.professional.slug) }, ctx.templates);
}

export function whatsappFor(client: Pick<Client, 'whatsapp'>, text: string): string {
  return whatsappUrl(client.whatsapp, text);
}

/** Dia local de um atendimento. */
export function appointmentDate(a: Pick<Appointment, 'start_utc'>, timeZone: string): LocalDate {
  return utcToZoned(a.start_utc, timeZone).date;
}

/**
 * Mensagem certa para o botão de WhatsApp do cartão (1 toque):
 * atendimento de amanhã → lembrete; já passou da hora → atraso; senão → confirmação.
 */
export function defaultMessageKind(
  a: Pick<Appointment, 'start_utc' | 'status'>,
  timeZone: string,
  now: number = Date.now(),
): 'confirmation' | 'reminder' | 'delay' | 'post_service' {
  if (a.status === 'completed') return 'post_service';
  const start = toMs(a.start_utc);
  const today = todayIn(timeZone, now);
  const day = utcToZoned(start, timeZone).date;
  if (day === today && now >= start - 10 * 60_000) return 'delay';
  if (day > today && day <= addDays(today, 1)) return 'reminder';
  return 'confirmation';
}
